import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api, API } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { StatusTimeline, StatusBadge } from "@/components/StatusTimeline";
import { meta } from "@/lib/statusMeta";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowLeft, ChevronRight, ChevronLeft, Printer, Upload, FolderUp, Folder, Mail, Copy, Loader2, Truck, Store, Send, Image as ImageIcon, Trash2, Download, Share,
} from "lucide-react";

export default function OrderDetail() {
  const { id } = useParams();
  const [o, setO] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(null);
  const [comment, setComment] = useState("");
  const [openFolder, setOpenFolder] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();
  const folderRef = useRef();

  const load = useCallback(async () => {
    try { const res = await api.get(`/orders/${id}`); setO(res.data); }
    catch { toast.error("Order not found"); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight") setLightbox((lb) => lb ? { ...lb, index: (lb.index + 1) % lb.list.length } : lb);
      else if (e.key === "ArrowLeft") setLightbox((lb) => lb ? { ...lb, index: (lb.index - 1 + lb.list.length) % lb.list.length } : lb);
      else if (e.key === "Escape") setLightbox(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!o) return <Layout><div className="py-24 text-center text-muted-foreground mono text-sm animate-pulse">Loading…</div></Layout>;

  const steps = o.steps || [];
  const idx = steps.indexOf(o.status);
  const nextStep = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null;
  const portalUrl = `${window.location.origin}/portal/${o.customer_token}`;

  const allPhotos = o.photos || [];
  const folderNames = [];
  const folderMap = {};
  const loose = [];
  for (const p of allPhotos) {
    if (p.folder) {
      if (!folderMap[p.folder]) { folderMap[p.folder] = []; folderNames.push(p.folder); }
      folderMap[p.folder].push(p);
    } else loose.push(p);
  }
  const folderPhotos = openFolder ? (folderMap[openFolder] || []) : [];
  const openScan = (list, index) => setLightbox({ list, index });
  const navScan = (dir) => setLightbox((lb) => lb ? { ...lb, index: (lb.index + dir + lb.list.length) % lb.list.length } : lb);
  const current = lightbox ? lightbox.list[lightbox.index] : null;
  const canShare = typeof navigator !== "undefined" && !!navigator.canShare;

  const downloadFolder = async (fname) => {
    try {
      const res = await api.get(`/orders/${id}/download-all`, { params: { folder: fname }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${(o.order_number || "order").replace("#", "")}_${fname}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Download failed"); }
  };

  const saveToPhotos = async (p) => {
    try {
      const res = await api.get(`/photos/${p.id}/download`, { responseType: "blob" });
      const file = new File([res.data], p.original_filename, { type: res.data.type || "image/jpeg" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: p.original_filename });
      } else {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement("a"); a.href = url; a.download = p.original_filename; a.click();
        URL.revokeObjectURL(url);
      }
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open share sheet"); }
  };

  const saveAllToPhotos = async (list) => {
    setSavingAll(true);
    try {
      const files = await Promise.all(list.map(async (p) => {
        const res = await api.get(`/photos/${p.id}/download`, { responseType: "blob" });
        return new File([res.data], p.original_filename, { type: res.data.type || "image/jpeg" });
      }));
      if (navigator.canShare && navigator.canShare({ files })) await navigator.share({ files, title: openFolder || "Scans" });
      else toast.error("This device can't save multiple photos at once — use Download instead");
    } catch (e) { if (e?.name !== "AbortError") toast.error("Couldn't open share sheet"); }
    finally { setSavingAll(false); }
  };

  const advance = async () => {
    setBusy(true);
    try { const r = await api.post(`/orders/${id}/advance`); setO((p) => ({ ...p, ...r.data })); toast.success(`Advanced to ${meta(r.data.status).label} · email sent`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not advance"); }
    finally { setBusy(false); }
  };

  const setStatus = async (status) => {
    setBusy(true);
    try { const r = await api.post(`/orders/${id}/status`, { status }); setO((p) => ({ ...p, ...r.data })); toast.success(`Set to ${meta(status).label} · email sent`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const upload = async (files) => {
    if (!files?.length) return;
    const valid = Array.from(files).filter((f) => {
      const n = f.name.toLowerCase();
      if (n.startsWith(".")) return false;
      return f.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|tiff?|pdf)$/.test(n);
    });
    if (!valid.length) { toast.error("No image files found in that folder"); return; }
    const folderName = valid[0].webkitRelativePath
      ? valid[0].webkitRelativePath.split("/")[0]
      : null;
    setUploading(true);
    setProgress({ percent: 0, speed: 0, loaded: 0, total: 0 });
    const fd = new FormData();
    valid.forEach((f) => fd.append("files", f));
    if (folderName) fd.append("folder", folderName);
    let lastTime = Date.now();
    let lastLoaded = 0;
    let speed = 0;
    try {
      const r = await api.post(`/orders/${id}/photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const now = Date.now();
          const dt = (now - lastTime) / 1000;
          if (dt >= 0.3) {
            speed = (e.loaded - lastLoaded) / dt;
            lastTime = now;
            lastLoaded = e.loaded;
          }
          setProgress({
            percent: e.total ? Math.round((e.loaded / e.total) * 100) : 0,
            speed,
            loaded: e.loaded,
            total: e.total || 0,
          });
        },
      });
      toast.success(`Uploaded ${r.data.uploaded} photo(s)`); load();
    }
    catch { toast.error("Upload failed"); }
    finally {
      setUploading(false); setProgress(null);
      if (fileRef.current) fileRef.current.value = "";
      if (folderRef.current) folderRef.current.value = "";
    }
  };

  const fmtBytes = (b) => {
    if (!b) return "0 B";
    const u = ["B", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(b) / Math.log(1024));
    return `${(b / Math.pow(1024, i)).toFixed(i ? 1 : 0)} ${u[i]}`;
  };

  const notifyPhotos = async () => {
    try { const r = await api.post(`/orders/${id}/notify-photos`); toast.success(`Photos-ready email sent (${r.data.photos} files)`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const delPhoto = async (pid) => {
    try { await api.delete(`/photos/${pid}`); load(); } catch { toast.error("Failed to delete"); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try { await api.post(`/orders/${id}/comments`, { message: comment }); setComment(""); load(); toast.success("Reply posted"); }
    catch { toast.error("Failed to post"); }
  };

  const copyPortal = () => { navigator.clipboard.writeText(portalUrl); toast.success("Customer link copied"); };
  const printQR = () => window.print();

  return (
    <Layout>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6" data-testid="back-btn">
        <ArrowLeft className="w-4 h-4" /> Back to orders
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-3xl font-bold mono">{o.order_number}</h1>
              <StatusBadge status={o.status} />
            </div>
            <div className="text-muted-foreground mt-1 flex items-center gap-3 text-sm">
              <span>{o.customer_name}</span><span className="text-border">·</span><span>{o.customer_email}</span>
              {o.customer_phone && <><span className="text-border">·</span><span className="mono text-xs">{o.customer_phone}</span></>}
              <span className="flex items-center gap-1 mono text-xs uppercase tracking-wider">
                {o.fulfillment_type === "pickup" ? <Store className="w-3.5 h-3.5" /> : <Truck className="w-3.5 h-3.5" />}
                {o.fulfillment_type === "pickup" ? "Pickup" : "Ship"}
              </span>
              {o.intake_label && <span className="mono text-xs px-1.5 py-0.5 rounded bg-secondary">{o.intake_label}</span>}
              {o.pickup_reminder_sent && <span className="mono text-xs px-1.5 py-0.5 rounded bg-secondary" style={{ color: "#10B981" }}>Pickup reminder sent</span>}
            </div>
          </div>
        </div>
        {nextStep && (
          <Button onClick={advance} disabled={busy} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="advance-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Advance to {meta(nextStep).label} <ChevronRight className="w-4 h-4 ml-1" /></>}
          </Button>
        )}
      </div>

      <div className="bg-card border border-border rounded-xl p-6 sm:p-8 mb-6">
        <StatusTimeline steps={steps} current={o.status} history={o.status_history} />
        <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-border">
          <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground self-center mr-2">Jump to</span>
          {steps.map((s) => (
            <button key={s} onClick={() => setStatus(s)} disabled={busy || s === o.status} data-testid={`set-status-${s}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors disabled:opacity-40 ${
                s === o.status ? "bg-brand text-primary-foreground" : "bg-secondary text-muted-foreground hover:text-foreground"
              }`}>{meta(s).label}</button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Photos */}
        <div className="lg:col-span-2 bg-card border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-brand" /> Digital Scans</h3>
            <div className="flex gap-2">
              <input ref={fileRef} type="file" multiple accept="image/*,.tif,.tiff" className="hidden"
                onChange={(e) => upload(e.target.files)} data-testid="photo-file-input" />
              <input ref={folderRef} type="file" webkitdirectory="" directory="" multiple className="hidden"
                onChange={(e) => upload(e.target.files)} data-testid="photo-folder-input" />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-photos-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}<span className="ml-1.5">Upload</span>
              </Button>
              <Button variant="outline" size="sm" onClick={() => folderRef.current?.click()} disabled={uploading} data-testid="upload-folder-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FolderUp className="w-4 h-4" />}<span className="ml-1.5">Upload folder</span>
              </Button>
              {o.photos?.length > 0 && (
                <Button size="sm" onClick={notifyPhotos} className="bg-brand hover:bg-brand-hover text-primary-foreground" data-testid="notify-photos-btn">
                  <Mail className="w-4 h-4 mr-1.5" /> Email customer
                </Button>
              )}
            </div>
          </div>
          {progress && (
            <div className="mb-4" data-testid="upload-progress">
              <div className="flex items-center justify-between mono text-[11px] text-muted-foreground mb-1.5">
                <span data-testid="upload-progress-percent">Uploading… {progress.percent}%</span>
                <span data-testid="upload-progress-stats">
                  {fmtBytes(progress.loaded)}{progress.total ? ` / ${fmtBytes(progress.total)}` : ""}
                  {progress.speed ? ` · ${fmtBytes(progress.speed)}/s` : ""}
                </span>
              </div>
              <div className="h-2 w-full rounded-full bg-secondary overflow-hidden">
                <div className="h-full bg-brand transition-all duration-200" style={{ width: `${progress.percent}%` }} />
              </div>
            </div>
          )}
          {allPhotos.length ? (
            <div className="space-y-4" data-testid="photo-grid">
              {folderNames.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {folderNames.map((name) => {
                    const items = folderMap[name];
                    return (
                      <button key={name} onClick={() => setOpenFolder(name)} data-testid={`folder-card-${name}`}
                        className="group text-left rounded-lg border border-border bg-secondary hover:border-brand/50 transition-colors p-4 flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-brand/15 flex items-center justify-center shrink-0">
                          <Folder className="w-5 h-5 text-brand" />
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-sm truncate">{name}</div>
                          <div className="mono text-[11px] text-muted-foreground">{items.length} scan{items.length === 1 ? "" : "s"}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
              {loose.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                  {loose.map((p, i) => (
                    <div key={p.id} className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary cursor-pointer" onClick={() => openScan(loose, i)}>
                      <img src={`${API}/photos/${p.id}/download`} alt={p.original_filename} className="w-full h-full object-cover" />
                      <button onClick={(e) => { e.stopPropagation(); delPhoto(p.id); }} data-testid={`delete-photo-${p.id}`}
                        className="absolute top-1.5 right-1.5 w-7 h-7 rounded-md bg-background/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-ruby">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div onClick={() => folderRef.current?.click()}
              className="border-2 border-dashed border-border rounded-lg py-12 text-center cursor-pointer hover:border-brand/50 transition-colors">
              <FolderUp className="w-7 h-7 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">Click to upload a folder of scanned negatives</p>
              <p className="mono text-[11px] text-muted-foreground mt-1">Or use “Upload” for individual files · Auto-deletes after 30 days</p>
            </div>
          )}
        </div>

        {/* QR + portal */}
        <div className="bg-card border border-border rounded-xl p-6 flex flex-col">
          <h3 className="font-heading text-lg font-semibold mb-4">Envelope QR</h3>
          <div className="print-area bg-white rounded-lg p-5 flex flex-col items-center text-center" data-testid="order-qr">
            <QRCodeSVG value={o.qr_token} size={150} level="M" />
            <div className="mono text-black text-sm font-bold mt-3">{o.order_number}</div>
            <div className="mono text-black/70 text-[11px] mt-0.5">{o.customer_name}</div>
            <div className="mono text-black/50 text-[9px] mt-1 uppercase tracking-wider">Low Five Film Lab</div>
          </div>
          <Button variant="outline" size="sm" className="mt-4 no-print" onClick={printQR} data-testid="print-qr-btn">
            <Printer className="w-4 h-4 mr-1.5" /> Print envelope label
          </Button>
          <div className="mt-5 pt-5 border-t border-border no-print">
            <span className="mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Customer link</span>
            <div className="flex gap-2 mt-2">
              <input readOnly value={portalUrl} className="flex-1 bg-secondary rounded-md px-2.5 py-2 text-xs mono text-muted-foreground truncate" data-testid="portal-link" />
              <Button variant="outline" size="sm" onClick={copyPortal} data-testid="copy-portal-btn"><Copy className="w-4 h-4" /></Button>
            </div>
          </div>
        </div>
      </div>

      {/* Comments */}
      <div className="bg-card border border-border rounded-xl p-6 mt-6 no-print">
        <h3 className="font-heading text-lg font-semibold mb-4">Customer Messages</h3>
        <div className="space-y-3 mb-4 max-h-72 overflow-y-auto" data-testid="comments-thread">
          {o.comments?.length ? o.comments.map((c) => (
            <div key={c.id} className={`flex ${c.role === "staff" ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${c.role === "staff" ? "bg-brand text-primary-foreground" : "bg-secondary"}`}>
                <div className="text-[11px] font-semibold opacity-70 mb-0.5">{c.name} · {c.role}</div>
                <div className="text-sm whitespace-pre-wrap">{c.message}</div>
              </div>
            </div>
          )) : <p className="text-sm text-muted-foreground">No messages yet.</p>}
        </div>
        <div className="flex gap-2">
          <Textarea value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Reply to the customer…" rows={2} data-testid="staff-comment-input" />
          <Button onClick={addComment} className="bg-brand hover:bg-brand-hover text-primary-foreground self-end" data-testid="staff-comment-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Folder viewer */}
      <Dialog open={!!openFolder} onOpenChange={(v) => !v && setOpenFolder(null)}>
        <DialogContent className="bg-card border-border max-w-4xl max-h-[85vh] overflow-y-auto no-print" data-testid="folder-viewer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Folder className="w-5 h-5 text-brand" /> {openFolder}</DialogTitle>
            <DialogDescription>{folderPhotos.length} scan{folderPhotos.length === 1 ? "" : "s"} · click a scan to view full size</DialogDescription>
          </DialogHeader>
          {openFolder && folderPhotos.length > 0 && (
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => downloadFolder(openFolder)} data-testid="download-folder-btn">
                <Download className="w-4 h-4 mr-1.5" /> Download this folder (.zip)
              </Button>
              {canShare && (
                <Button size="sm" variant="outline" onClick={() => saveAllToPhotos(folderPhotos)} disabled={savingAll} data-testid="save-all-to-photos-btn">
                  {savingAll ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Share className="w-4 h-4 mr-1.5" />} Save all to Photos
                </Button>
              )}
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {folderPhotos.map((p, i) => (
              <div key={p.id} className="group relative aspect-square rounded-lg overflow-hidden border border-border bg-secondary cursor-pointer" onClick={() => openScan(folderPhotos, i)} data-testid={`folder-scan-${p.id}`}>
                <img src={`${API}/photos/${p.id}/download`} alt={p.original_filename} className="w-full h-full object-cover" />
                <button onClick={(e) => { e.stopPropagation(); delPhoto(p.id); }}
                  className="absolute top-1.5 right-1.5 w-7 h-7 rounded-md bg-background/80 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-ruby">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Lightbox with swipe */}
      <Dialog open={!!current} onOpenChange={(v) => !v && setLightbox(null)}>
        <DialogContent className="bg-card border-border max-w-4xl p-2 no-print" data-testid="scan-lightbox">
          <DialogHeader className="sr-only">
            <DialogTitle>Scan preview</DialogTitle>
            <DialogDescription>View, navigate and download this scan</DialogDescription>
          </DialogHeader>
          {current && (
            <div className="flex flex-col">
              <div className="relative flex items-center justify-center"
                onTouchStart={(e) => { e.currentTarget._x = e.touches[0].clientX; }}
                onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (e.currentTarget._x || 0); if (Math.abs(dx) > 50) navScan(dx < 0 ? 1 : -1); }}>
                <img src={`${API}/photos/${current.id}/download`} alt={current.original_filename} className="w-full max-h-[72vh] object-contain rounded-lg" />
                {lightbox.list.length > 1 && (
                  <>
                    <button onClick={() => navScan(-1)} data-testid="lightbox-prev"
                      className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background">
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => navScan(1)} data-testid="lightbox-next"
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-background">
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </>
                )}
              </div>
              <div className="flex items-center justify-between p-3 gap-3">
                <span className="mono text-xs text-muted-foreground truncate">
                  {current.original_filename}{lightbox.list.length > 1 ? ` · ${lightbox.index + 1}/${lightbox.list.length}` : ""}
                </span>
                <div className="flex items-center gap-2 shrink-0">
                  {canShare && (
                    <Button size="sm" variant="outline" onClick={() => saveToPhotos(current)} data-testid="save-to-photos-btn">
                      <Share className="w-4 h-4 mr-1.5" /> Save to Photos
                    </Button>
                  )}
                  <a href={`${API}/photos/${current.id}/download`} download={current.original_filename} target="_blank" rel="noreferrer">
                    <Button size="sm" className="bg-brand hover:bg-brand-hover text-primary-foreground"><Download className="w-4 h-4 mr-1.5" /> Download</Button>
                  </a>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
